import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface LoginSession {
  id: string;
  device: string;
  location: string;
  ipAddress: string;
  timestamp: string;
  isCurrentSession: boolean;
  status: 'successful' | 'failed' | 'suspicious';
  browser?: string;
  os?: string;
}

const LoginHistory: React.FC = () => {
  const colors = getThemeColors('dark');

  const [loginHistory, setLoginHistory] = useState<LoginSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'successful' | 'failed' | 'suspicious'>('all');

  useEffect(() => {
    loadLoginHistory();
  }, []);

  const loadLoginHistory = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const mockHistory: LoginSession[] = [
        {
          id: '1',
          device: 'iPhone 13 Pro',
          location: 'Nairobi, Kenya',
          ipAddress: '102.68.23.45',
          timestamp: '2024-01-15T10:30:00Z',
          isCurrentSession: true,
          status: 'successful',
          browser: 'Safari',
          os: 'iOS 17.2',
        },
        {
          id: '2',
          device: 'MacBook Pro',
          location: 'Nairobi, Kenya',
          ipAddress: '102.68.23.45',
          timestamp: '2024-01-15T08:15:00Z',
          isCurrentSession: false,
          status: 'successful',
          browser: 'Chrome',
          os: 'macOS 13.1',
        },
        {
          id: '3',
          device: 'Unknown Device',
          location: 'Unknown Location',
          ipAddress: '203.45.67.89',
          timestamp: '2024-01-14T22:45:00Z',
          isCurrentSession: false,
          status: 'suspicious',
          browser: 'Unknown',
          os: 'Unknown',
        },
        {
          id: '4',
          device: 'Samsung Galaxy S23',
          location: 'Nairobi, Kenya',
          ipAddress: '102.68.23.45',
          timestamp: '2024-01-14T14:20:00Z',
          isCurrentSession: false,
          status: 'successful',
          browser: 'Chrome Mobile',
          os: 'Android 13',
        },
        {
          id: '5',
          device: 'Windows PC',
          location: 'Nairobi, Kenya',
          ipAddress: '102.68.23.45',
          timestamp: '2024-01-13T16:10:00Z',
          isCurrentSession: false,
          status: 'failed',
          browser: 'Edge',
          os: 'Windows 11',
        },
      ];

      setLoginHistory(mockHistory);
    } catch (error) {
      console.error('Failed to load login history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

      if (diffInHours < 24) {
        return `${diffInHours} hours ago`;
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        });
      }
    } catch (error) {
      return 'Unknown';
    }
  };

  const formatTime = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case 'successful': return '✅';
      case 'failed': return '❌';
      case 'suspicious': return '⚠️';
      default: return '❓';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'successful': return colors.success;
      case 'failed': return colors.error;
      case 'suspicious': return colors.warning;
      default: return colors.textSecondary;
    }
  };

  const handleTerminateSession = (sessionId: string) => {
    if (window.confirm('Are you sure you want to terminate this session?')) {
      setLoginHistory(prev =>
        prev.map(session =>
          session.id === sessionId
            ? { ...session, status: 'failed' as const }
            : session
        )
      );
      alert('Session terminated successfully.');
    }
  };

  const filteredHistory = loginHistory.filter(session => {
    if (filter === 'all') return true;
    return session.status === filter;
  });

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: spacing.lg,
    },
    header: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
      textAlign: 'center',
    },
    headerIcon: {
      fontSize: 48,
      marginBottom: spacing.md,
    },
    headerTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    headerSubtitle: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      marginBottom: spacing.xl,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('lg'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.lg,
      paddingBottom: spacing.md,
      borderBottom: `2px solid ${colors.primary}`,
    },
    filterContainer: {
      display: 'flex',
      gap: spacing.md,
      marginBottom: spacing.lg,
      overflowX: 'auto',
      paddingBottom: spacing.sm,
    },
    filterChip: {
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.full,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    filterChipActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    filterText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
    },
    sessionCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.md,
      border: `1px solid ${colors.border}`,
      ...getShadowStyle('md'),
    },
    sessionCardCurrent: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '05',
    },
    sessionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.md,
    },
    sessionInfo: {
      flex: 1,
    },
    sessionDevice: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    sessionDetails: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    sessionMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      marginBottom: spacing.sm,
    },
    sessionStatus: {
      display: 'flex',
      alignItems: 'center',
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
    },
    sessionTime: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    currentBadge: {
      backgroundColor: colors.primary + '20',
      color: colors.primary,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      marginLeft: spacing.sm,
    },
    sessionActions: {
      display: 'flex',
      gap: spacing.sm,
      marginTop: spacing.md,
    },
    actionButton: {
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.md,
      border: 'none',
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      ...getShadowStyle('sm'),
      transition: 'all 0.2s ease',
    },
    terminateButton: {
      backgroundColor: colors.error,
      color: colors.white,
    },
    detailsButton: {
      backgroundColor: colors.primary,
      color: colors.white,
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    emptyIcon: {
      fontSize: 64,
      marginBottom: spacing.lg,
      opacity: 0.5,
    },
    emptyText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    emptySubtext: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 1.5,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
    skeletonCard: {
      height: 120,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.surface,
      marginBottom: spacing.md,
      padding: spacing.md,
    },
    skeletonLine: {
      height: 12,
      borderRadius: 6,
      backgroundColor: colors.border,
      marginBottom: spacing.sm,
    },
    securityNote: {
      backgroundColor: colors.warning + '10',
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      border: `1px solid ${colors.warning}`,
      marginTop: spacing.lg,
    },
    securityNoteTitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      color: colors.warning,
      marginBottom: spacing.sm,
    },
    securityNoteText: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      lineHeight: 1.5,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <UserDashboardHeader
          title="Login History"
          subtitle="Monitor your account access"
          showBackButton={true}
          showHomeButton={true}
        />
        <div style={styles.loadingContainer}>
          <div style={styles.skeletonCard}>
            <div style={{ ...styles.skeletonLine, width: '70%' }} />
            <div style={{ ...styles.skeletonLine, width: '50%' }} />
            <div style={{ ...styles.skeletonLine, width: '30%' }} />
          </div>
          <div style={styles.skeletonCard}>
            <div style={{ ...styles.skeletonLine, width: '60%' }} />
            <div style={{ ...styles.skeletonLine, width: '40%' }} />
            <div style={{ ...styles.skeletonLine, width: '80%' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Login History"
        subtitle="Monitor your account access"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>📋</div>
            <h1 style={styles.headerTitle}>Login History</h1>
            <p style={styles.headerSubtitle}>Monitor your account access and security</p>
          </div>

          {/* Filters */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Filter Sessions</h2>
            <div style={styles.filterContainer}>
              {[
                { id: 'all', label: 'All Sessions', count: loginHistory.length },
                { id: 'successful', label: 'Successful', count: loginHistory.filter(s => s.status === 'successful').length },
                { id: 'failed', label: 'Failed', count: loginHistory.filter(s => s.status === 'failed').length },
                { id: 'suspicious', label: 'Suspicious', count: loginHistory.filter(s => s.status === 'suspicious').length },
              ].map((filterOption) => (
                <div
                  key={filterOption.id}
                  style={{
                    ...styles.filterChip,
                    ...(filter === filterOption.id ? styles.filterChipActive : {}),
                  }}
                  onClick={() => setFilter(filterOption.id as any)}
                >
                  <span style={styles.filterText}>
                    {filterOption.label} ({filterOption.count})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Login Sessions */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>
              Login Sessions ({filteredHistory.length})
            </h2>

            {filteredHistory.length > 0 ? (
              filteredHistory.map((session) => (
                <div
                  key={session.id}
                  style={{
                    ...styles.sessionCard,
                    ...(session.isCurrentSession ? styles.sessionCardCurrent : {}),
                  }}
                >
                  <div style={styles.sessionHeader}>
                    <div style={styles.sessionInfo}>
                      <div style={styles.sessionDevice}>
                        {session.device}
                        {session.isCurrentSession && (
                          <span style={styles.currentBadge}>Current Session</span>
                        )}
                      </div>
                      <div style={styles.sessionDetails}>
                        📍 {session.location} • IP: {session.ipAddress}
                      </div>
                      <div style={styles.sessionMeta}>
                        <div
                          style={{
                            ...styles.sessionStatus,
                            backgroundColor: getStatusColor(session.status) + '20',
                            color: getStatusColor(session.status),
                          }}
                        >
                          <span style={{ marginRight: spacing.xs }}>
                            {getStatusIcon(session.status)}
                          </span>
                          {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                        </div>
                        <div style={styles.sessionTime}>
                          {formatDate(session.timestamp)} at {formatTime(session.timestamp)}
                        </div>
                      </div>
                      {(session.browser || session.os) && (
                        <div style={styles.sessionDetails}>
                          {session.browser && session.os ? `${session.browser} on ${session.os}` : (session.browser || session.os)}
                        </div>
                      )}
                    </div>
                  </div>

                  {!session.isCurrentSession && session.status === 'successful' && (
                    <div style={styles.sessionActions}>
                      <button
                        style={{ ...styles.actionButton, ...styles.terminateButton }}
                        onClick={() => handleTerminateSession(session.id)}
                      >
                        Terminate Session
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🔍</div>
                <div style={styles.emptyText}>No sessions found</div>
                <div style={styles.emptySubtext}>
                  Try adjusting your filters to see more login sessions.
                </div>
              </div>
            )}
          </div>

          {/* Security Note */}
          <div style={styles.securityNote}>
            <div style={styles.securityNoteTitle}>🛡️ Security Reminder</div>
            <div style={styles.securityNoteText}>
              • Regularly review your login history for suspicious activity<br/>
              • Terminate sessions from devices you no longer use<br/>
              • Enable two-factor authentication for enhanced security<br/>
              • Contact support immediately if you notice unauthorized access
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginHistory;